import { ScrollArea, Spinner } from '@ibsys/kit';
import { FC, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteAiAgentChats } from '@/entities/ai-agent-chat';
import { useCopilot } from '@/features/copilot';
import { useGuardedInfiniteScroll } from '@/shared/hooks';
import { InfiniteScrollSentinel } from '@/shared/ui';
import { CopilotChatCard } from './CopilotChatCard';

/** Упреждение подгрузки следующей порции списка, px */
const CHATS_LOAD_THRESHOLD = 300;

export const CopilotChatListView: FC = () => {
  const { t } = useTranslation(['aiAgent']);
  const { openChat } = useCopilot();
  const viewportRef = useRef<HTMLDivElement>(null);
  const { chats, data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteAiAgentChats();

  const { sentinelRef, isAutoLoadStopped, resumeAutoLoad } = useGuardedInfiniteScroll({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    pagesCount: data?.pages.length ?? 0,
    threshold: CHATS_LOAD_THRESHOLD,
    root: viewportRef,
  });

  const handleChatClick = useCallback(
    (chatId: number) => {
      openChat(chatId);
    },
    [openChat],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center flex-1">
        <Spinner className="size-8" />
      </div>
    );
  }

  if (!chats.length) {
    return (
      <div className="flex items-center justify-center  flex-1">
        <p className="text-muted-foreground">{t('aiAgent:emptyChatList')}</p>
      </div>
    );
  }

  return (
    <ScrollArea className="flex-1 min-h-0" viewPortProps={{ ref: viewportRef }}>
      <div className="flex flex-col gap-2 p-2">
        {chats.map((chat) => (
          <CopilotChatCard key={chat.id} chat={chat} onClick={handleChatClick} />
        ))}
        <InfiniteScrollSentinel
          sentinelRef={sentinelRef}
          isFetchingNextPage={isFetchingNextPage}
          isAutoLoadStopped={isAutoLoadStopped}
          onRetry={resumeAutoLoad}
        />
      </div>
    </ScrollArea>
  );
};
